import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap } from 'rxjs/operators';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { PeriodKpiTotals } from '../../../proxies/monthly-cost.proxy.service';
import { CostReportsProxyService, PeriodSummaryReportDto } from '../../../proxies/cost-reports.proxy.service';
import { CostReportsCacheService } from '../services/cost-reports-cache.service';
import { buildSearchCommand, hasActiveFilters as filtersAreActive } from '../utils/filter-command.util';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { FabricProxyService } from '../../../proxies/fabric.proxy.service';
import { LabDipRequestProxyService } from '../../../proxies/lab-dip-request.proxy.service';
import { WorkOrderProxyService } from '../../../proxies/work-order.proxy.service';
import { ProductionCardProxyService } from '../../../proxies/production-card.proxy.service';
import { RollProxyService } from '../../../proxies/roll.proxy.service';
import { CostSourceFilter, ORDER_TYPE_LABELS, OrderType, ProcessDetailContext } from '../models/cost-analysis.models';
import { CostAnalysisFilters, CostAnalysisStateService } from '../services/cost-analysis-state.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { CostingRoles } from '../../../models/roles/roles-group';
import { FabricSummaryComponent } from '../fabric-summary/fabric-summary.component';
import { DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';

const AUTOCOMPLETE_DEBOUNCE_MS = 300;

type TabKey = 'by-process' | 'by-fabric' | 'by-component';

// 'by-component' is intentionally not rendered (hidden for all users); kept in
// the TabKey union for back-compat with any persisted state value.
const TAB_NAMES: readonly TabKey[] = ['by-process', 'by-fabric'];

type FilterName = 'fabric' | 'color' | 'client' | 'orderType' | 'productionCard' | 'workOrder' | 'roll';

@Component({
  selector: 'app-cost-analysis-page',
  templateUrl: './cost-analysis-page.component.html',
  styleUrl: './cost-analysis-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostAnalysisPageComponent implements OnInit {
  private readonly costReportsProxy = inject(CostReportsProxyService);
  private readonly costReportsCache = inject(CostReportsCacheService);
  private readonly fabricProxy = inject(FabricProxyService);
  private readonly labDipProxy = inject(LabDipRequestProxyService);
  private readonly businessProxy = inject(BusinessProxyService);
  private readonly workOrderProxy = inject(WorkOrderProxyService);
  private readonly productionCardProxy = inject(ProductionCardProxyService);
  private readonly rollProxy = inject(RollProxyService);
  private readonly state = inject(CostAnalysisStateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userAuth = inject(UserAuthenticationService);
  private readonly translate = inject(TranslateService);

  // Standard rates are the open-by-default view for any authenticated user.
  // Real / Estimated sources expose actual company costs, so they unlock only
  // with the costing-admin role. Roles are static per session, so a plain
  // field (mirrors businesses.component.ts) is enough — no signal needed.
  readonly canViewRealCosts = this.userAuth.roles.includes(CostingRoles.FinanceCostingAdmin);

  readonly yearControl = new FormControl<number>(this.state.selectedYear(), [
    Validators.required,
    Validators.min(2020),
    Validators.max(2099),
  ]);
  readonly monthControl = new FormControl<number>(this.state.selectedMonth(), [Validators.required, Validators.min(1), Validators.max(12)]);
  readonly monthOptions: { label: string; key: string; value: number }[] = [
    { label: 'January', key: 'Month_Jan', value: 1 },
    { label: 'February', key: 'Month_Feb', value: 2 },
    { label: 'March', key: 'Month_Mar', value: 3 },
    { label: 'April', key: 'Month_Apr', value: 4 },
    { label: 'May', key: 'Month_May', value: 5 },
    { label: 'June', key: 'Month_Jun', value: 6 },
    { label: 'July', key: 'Month_Jul', value: 7 },
    { label: 'August', key: 'Month_Aug', value: 8 },
    { label: 'September', key: 'Month_Sep', value: 9 },
    { label: 'October', key: 'Month_Oct', value: 10 },
    { label: 'November', key: 'Month_Nov', value: 11 },
    { label: 'December', key: 'Month_Dec', value: 12 },
  ];

  readonly costSourceControl = new FormControl<CostSourceFilter>(this.canViewRealCosts ? this.state.costSourceFilter() : 'Standard', {
    nonNullable: true,
  });
  readonly costSourceOptions: { label: string; translationKey: string; value: CostSourceFilter }[] = this.canViewRealCosts
    ? [
        { label: 'Estimated', translationKey: 'Cost_Analysis_Cost_Source_Estimated', value: 'Estimated' },
        { label: 'Real', translationKey: 'Cost_Analysis_Cost_Source_Real', value: 'Real' },
        { label: 'Standard', translationKey: 'Cost_Analysis_Cost_Source_Standard', value: 'Standard' },
      ]
    : [{ label: 'Standard', translationKey: 'Cost_Analysis_Cost_Source_Standard', value: 'Standard' }];

  readonly fabricFilterControl = new FormControl<string[]>(this.state.filters().fabricCodes, { nonNullable: true });
  readonly colorFilterControl = new FormControl<string[]>(this.state.filters().colorReferences, { nonNullable: true });
  readonly productionCardFilterControl = new FormControl<string[]>(this.state.filters().productionCardNumbers, { nonNullable: true });
  readonly workOrderFilterControl = new FormControl<string[]>(this.state.filters().workOrderReferences, { nonNullable: true });
  readonly rollFilterControl = new FormControl<string[]>(this.state.filters().rollNumbers, { nonNullable: true });
  readonly clientFilterControl = new FormControl<string[]>(this.state.filters().customerReferences, { nonNullable: true });
  readonly orderTypeFilterControl = new FormControl<OrderType[]>(this.state.filters().orderTypes, { nonNullable: true });
  readonly onlyCompletedRollsControl = new FormControl<boolean>(this.state.filters().onlyCompletedRolls, { nonNullable: true });

  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'cost-analysis-filters',
    config: this.filterConfig,
    defaults: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      costSource: this.canViewRealCosts ? 'Estimated' : 'Standard',
    },
  });
  private filtersMounted = false;
  readonly filtersCollapsed = signal<boolean>(localStorage.getItem('cost-analysis-filters-collapsed') === '1');

  readonly activeFilterCount = computed(() => {
    const f = this.state.filters();
    return (
      f.fabricCodes.length +
      f.colorReferences.length +
      f.productionCardNumbers.length +
      f.workOrderReferences.length +
      f.rollNumbers.length +
      f.customerReferences.length +
      f.orderTypes.length +
      (f.onlyCompletedRolls ? 1 : 0)
    );
  });

  toggleFiltersCollapsed(): void {
    const next = !this.filtersCollapsed();
    this.filtersCollapsed.set(next);
    localStorage.setItem('cost-analysis-filters-collapsed', next ? '1' : '0');
  }

  readonly valuesHidden = signal<boolean>(localStorage.getItem('cost-analysis-values-hidden') === '1');

  toggleValuesHidden(): void {
    const next = !this.valuesHidden();
    this.valuesHidden.set(next);
    localStorage.setItem('cost-analysis-values-hidden', next ? '1' : '0');
  }

  maskValue(value: string): string {
    return this.valuesHidden() ? '*****' : value;
  }

  readonly orderTypeFilterOptions: { value: OrderType; label: string }[] = [
    { value: OrderType.Sample, label: ORDER_TYPE_LABELS[OrderType.Sample] },
    { value: OrderType.Bulk, label: ORDER_TYPE_LABELS[OrderType.Bulk] },
    { value: OrderType.Greige, label: ORDER_TYPE_LABELS[OrderType.Greige] },
    { value: OrderType.ShortFall, label: ORDER_TYPE_LABELS[OrderType.ShortFall] },
    { value: OrderType.Linked, label: ORDER_TYPE_LABELS[OrderType.Linked] },
    { value: OrderType.BGrade, label: ORDER_TYPE_LABELS[OrderType.BGrade] },
  ];

  private readonly loadingCount = signal(0);
  readonly loading = computed(() => this.loadingCount() > 0);
  readonly activeTab = signal<TabKey>(this.state.activeTab());
  readonly activeTabIndex = computed(() => Math.max(0, TAB_NAMES.indexOf(this.activeTab())));
  readonly notCalculated = signal(false);

  private readonly fabricSummary = viewChild(FabricSummaryComponent);
  readonly fabricCount = computed<number | null>(() => this.fabricSummary()?.result()?.totalCount ?? null);
  readonly allFabricsExpanded = computed(() => this.fabricSummary()?.allFabricsExpanded() ?? false);

  toggleAllFabrics(): void {
    this.fabricSummary()?.toggleAllFabrics();
  }

  readonly periodSummary = signal<PeriodSummaryReportDto | null>(null);
  readonly byProcessReport = this.state.byProcessReport;
  readonly stateCostSourceFilter = this.state.costSourceFilter;

  readonly periodTotals = signal<PeriodKpiTotals>({
    rollCount: 0,
    productionLbs: 0,
    processedLbs: 0,
  });

  readonly hasData = computed(() => this.periodSummary() !== null);

  readonly productionLbs = computed(() => this.periodTotals().productionLbs);

  readonly realCostPerLbUsd = computed(() => {
    const rep = this.byProcessReport();
    if (!rep) return 0;
    return rep.processRows.reduce((sum, r) => sum + r.realCostPerLbUsd, 0);
  });

  readonly standardCostPerLbUsd = computed(() => {
    const rep = this.byProcessReport();
    if (!rep) return 0;
    return rep.processRows.reduce((sum, r) => sum + r.standardCostPerLbUsd, 0);
  });

  readonly varianceCostPerLbUsd = computed(() => this.realCostPerLbUsd() - this.standardCostPerLbUsd());

  readonly variancePct = computed(() => {
    const std = this.standardCostPerLbUsd();
    if (std <= 0) return null;
    return (this.varianceCostPerLbUsd() / std) * 100;
  });

  readonly varianceTone = computed<'positive' | 'negative' | 'default'>(() => {
    const v = this.varianceCostPerLbUsd();
    if (v > 0.0001) return 'negative';
    if (v < -0.0001) return 'positive';
    return 'default';
  });

  readonly fabricFilterOptions = computed(() => {
    const rep = this.byProcessReport();
    if (!rep) return [];
    return rep.fabricMatrix
      .map((f) => ({
        value: f.fabricCode,
        label: f.fabricDescription && f.fabricDescription !== f.fabricCode ? `${f.fabricDescription} (${f.fabricCode})` : f.fabricCode,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  private readonly _colorOptions = signal<{ value: string; label: string }[]>([]);
  private readonly _productionCardOptions = signal<{ value: string; label: string }[]>([]);
  private readonly _workOrderOptions = signal<{ value: string; label: string }[]>([]);
  private readonly _rollOptions = signal<{ value: string; label: string }[]>([]);
  private readonly _clientOptions = signal<{ value: string; label: string }[]>([]);

  readonly colorFilterOptions = this._colorOptions.asReadonly();
  readonly productionCardFilterOptions = this._productionCardOptions.asReadonly();
  readonly workOrderFilterOptions = this._workOrderOptions.asReadonly();
  readonly rollFilterOptions = this._rollOptions.asReadonly();
  readonly clientFilterOptions = this._clientOptions.asReadonly();

  private readonly colorSearch$ = new Subject<string>();
  private readonly productionCardSearch$ = new Subject<string>();
  private readonly workOrderSearch$ = new Subject<string>();
  private readonly rollSearch$ = new Subject<string>();
  private readonly clientSearch$ = new Subject<string>();

  onFilterAutocomplete(name: FilterName, event: { filter: string }): void {
    const q = event.filter ?? '';
    switch (name) {
      case 'color':
        this.colorSearch$.next(q);
        break;
      case 'productionCard':
        this.productionCardSearch$.next(q);
        break;
      case 'workOrder':
        this.workOrderSearch$.next(q);
        break;
      case 'roll':
        this.rollSearch$.next(q);
        break;
      case 'client':
        this.clientSearch$.next(q);
        break;
    }
  }

  private readonly _currentYear = signal<number>(this.yearControl.value ?? new Date().getFullYear());
  private readonly _currentMonth = signal<number>(this.monthControl.value ?? new Date().getMonth() + 1);
  readonly currentYear = this._currentYear.asReadonly();
  readonly currentMonth = this._currentMonth.asReadonly();

  readonly periodLabel = computed(() => {
    const ps = this.periodSummary();
    if (!ps) return '';
    const monthOption = this.monthOptions.find((m) => m.value === ps.month);
    const monthLabel = monthOption?.label ?? `M${ps.month}`;
    return `${monthLabel} ${ps.year} · v${ps.iteration}`;
  });

  readonly periodTranslationKey = computed(() => {
    const ps = this.periodSummary();
    if (!ps) return '';
    const monthOption = this.monthOptions.find((m) => m.value === ps.month);
    return monthOption?.key ?? '';
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.syncFilterControls(filters));
  }

  ngOnInit(): void {
    this.wireLoadPipeline();
    if (!this.canViewRealCosts) {
      // Lock the whole module to Standard: seed the state before any load and
      // disable the (Standard-only) dropdown so it can't be changed.
      this.state.costSourceFilter.set('Standard');
      this.costSourceControl.disable({ emitEvent: false });
    }
    this.filterConfig.set(this.buildFilterConfig());

    this.yearControl.valueChanges.subscribe((value) => {
      if (value === null || value === undefined) return;
      this._currentYear.set(value);
      this.state.setPeriod(value, this._currentMonth());
    });
    this.monthControl.valueChanges.subscribe((value) => {
      if (value === null || value === undefined) return;
      this._currentMonth.set(value);
      this.state.setPeriod(this._currentYear(), value);
    });

    this.costSourceControl.valueChanges.subscribe((value) => {
      this.state.costSourceFilter.set(this.canViewRealCosts ? (value ?? 'Estimated') : 'Standard');
    });

    const wirePicker = <T>(control: FormControl<T[]>, key: keyof CostAnalysisFilters): void => {
      control.valueChanges.subscribe((selection) => {
        const next = { ...this.state.filters(), [key]: (selection ?? []) as unknown as string[] } as CostAnalysisFilters;
        this.state.filters.set(next);
      });
    };
    wirePicker(this.fabricFilterControl, 'fabricCodes');
    wirePicker(this.colorFilterControl, 'colorReferences');
    wirePicker(this.productionCardFilterControl, 'productionCardNumbers');
    wirePicker(this.workOrderFilterControl, 'workOrderReferences');
    wirePicker(this.rollFilterControl, 'rollNumbers');
    wirePicker(this.clientFilterControl, 'customerReferences');
    wirePicker(this.orderTypeFilterControl, 'orderTypes');

    // Unlike the pickers, this flag also changes the period-summary KPIs, so
    // toggling it refetches that summary too (reports reload via filters()).
    this.onlyCompletedRollsControl.valueChanges.subscribe((value) => {
      if (this.state.filters().onlyCompletedRolls === value) return;
      this.state.filters.set({ ...this.state.filters(), onlyCompletedRolls: value });
      this.reloadPeriodSummary();
    });

    this.wireFilterAutocomplete();
    this.restoreFromCacheIfPresent();
  }

  private restoreFromCacheIfPresent(): void {
    const year = this.yearControl.value;
    const month = this.monthControl.value;
    if (!year || !month) return;
    const src = this.state.costSourceFilter();
    const onlyCompleted = this.state.filters().onlyCompletedRolls;
    if (!this.costReportsCache.hasPeriodSummary(year, month, src, onlyCompleted)) return;

    forkJoin([
      this.costReportsCache
        .getPeriodSummary(year, month, src, onlyCompleted)
        .pipe(catchError(() => of({ success: false, data: null, errorMessage: null }))),
      this.costReportsCache
        .getProcessSummary(year, month, src)
        .pipe(catchError(() => of({ success: false, data: null, errorMessage: null }))),
    ]).subscribe(([periodResp, processResp]) => {
      this.applyPeriodSummary(periodResp.success && periodResp.data ? periodResp.data : null);
      this.byProcessReport.set(processResp.success && processResp.data ? processResp.data : null);
    });
  }

  private wireFilterAutocomplete(): void {
    this.colorSearch$
      .pipe(
        debounceTime(AUTOCOMPLETE_DEBOUNCE_MS),
        switchMap((q) => this.labDipProxy.searchColors(q).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resp) => {
        const items = resp?.success && resp.data ? resp.data : [];
        this._colorOptions.set(
          items
            .filter((c) => !!c.id)
            .slice(0, 50)
            .map((c) => ({ value: c.id, label: c.labDipNumber ? `${c.name} · ${c.labDipNumber}` : c.name })),
        );
      });

    this.productionCardSearch$
      .pipe(
        debounceTime(AUTOCOMPLETE_DEBOUNCE_MS),
        switchMap((q) => this.productionCardProxy.search(q).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resp) => {
        const items = resp?.success && resp.data?.items ? resp.data.items : [];
        this._productionCardOptions.set(
          items
            .filter((pc) => !!pc.productionCard?.number)
            .map((pc) => ({ value: pc.productionCard!.number!, label: pc.productionCard!.number! })),
        );
      });

    this.workOrderSearch$
      .pipe(
        debounceTime(AUTOCOMPLETE_DEBOUNCE_MS),
        switchMap((q) => this.workOrderProxy.search(q).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resp) => {
        const items = resp?.success && resp.data?.items ? resp.data.items : [];
        this._workOrderOptions.set(items.filter((w) => !!w.id).map((w) => ({ value: w.id, label: w.orderNumber ?? w.id.slice(0, 8) })));
      });

    this.rollSearch$
      .pipe(
        debounceTime(AUTOCOMPLETE_DEBOUNCE_MS),
        switchMap((q) => this.rollProxy.search(q).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resp) => {
        const items = resp?.success && resp.data?.items ? resp.data.items : [];
        this._rollOptions.set(items.filter((r) => !!r.number).map((r) => ({ value: r.number!, label: r.number! })));
      });

    this.clientSearch$
      .pipe(
        debounceTime(AUTOCOMPLETE_DEBOUNCE_MS),
        switchMap((q) => this.businessProxy.search({ name: q, businessTypes: [1] }).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resp) => {
        const items = resp?.success && resp.data?.items ? resp.data.items : [];
        this._clientOptions.set(items.filter((b) => !!b.id).map((b) => ({ value: b.id!, label: b.name })));
      });
  }

  setTab(tab: TabKey): void {
    this.activeTab.set(tab);
    this.state.activeTab.set(tab);
  }

  onTabNameChange(name: string | undefined): void {
    if (!name) return;
    if ((TAB_NAMES as readonly string[]).includes(name)) {
      this.setTab(name as TabKey);
    }
  }

  private readonly loadTrigger$ = new Subject<void>();

  load(): void {
    // Belt-and-suspenders sync: csl-dynamic-filters restores persisted year/
    // month via patchValue({ emitEvent: false }), which never runs
    // yearControl/monthControl's valueChanges — so _currentYear/_currentMonth
    // (what by-process/by-fabric actually receive as [year]/[month]) could
    // otherwise still be today's date even though the dropdown already shows
    // the restored value. Re-read the live control values here so Apply is
    // always correct regardless of how the form got to its current value.
    const year = this.yearControl.value;
    const month = this.monthControl.value;
    if (year != null) this._currentYear.set(year);
    if (month != null) this._currentMonth.set(month);
    this.state.requestReload();
    this.loadTrigger$.next();
  }

  private wireLoadPipeline(): void {
    this.loadTrigger$
      .pipe(
        switchMap(() => {
          const year = this.yearControl.value;
          const month = this.monthControl.value;
          if (!year || !month) return of(null);

          this.state.setPeriod(year, month);
          this.startLoading();
          this.notCalculated.set(false);
          this.costReportsCache.invalidatePeriod(year, month);

          const src = this.state.costSourceFilter();
          const filters = this.state.filters();
          const cmd = buildSearchCommand(src, filters);

          const processObs = cmd
            ? this.costReportsProxy.searchProcessSummaryReport(year, month, cmd, filters.onlyCompletedRolls)
            : this.costReportsCache.getProcessSummary(year, month, src);

          return forkJoin([
            this.costReportsCache.getPeriodSummary(year, month, src, filters.onlyCompletedRolls).pipe(
              catchError((err) => {
                this.handlePeriodSummaryError(err);
                return of({ success: false, data: null, errorMessage: null });
              }),
            ),
            processObs.pipe(catchError(() => of({ success: false, data: null, errorMessage: null }))),
          ]).pipe(finalize(() => this.stopLoading()));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (!result) return;
        const [periodResp, processResp] = result;
        this.applyPeriodSummary(periodResp.success && periodResp.data ? periodResp.data : null);
        this.byProcessReport.set(processResp.success && processResp.data ? processResp.data : null);
      });
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translation = this.translate.instant(key);
    return translation === key ? fallback : translation;
  }

  private buildFilterConfig(): DynamicFilterConfig {
    const monthFilterOptions = this.monthOptions.map((m) => ({
      label: this.translateOrFallback(m.key, m.label),
      value: m.value,
    }));
    const costSourceFilters: DynamicFilterConfig['filters'] = this.canViewRealCosts
      ? [
          {
            type: 'select',
            formControlName: 'costSource',
            label: this.translateOrFallback('Cost_Analysis_Cost_Source', 'Cost Source'),
            options: this.costSourceOptions.map((o) => ({ label: this.translateOrFallback(o.translationKey, o.label), value: o.value })),
            showClear: false,
            defaultVisible: true,
            hideable: false,
          },
        ]
      : [];
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'number',
          formControlName: 'year',
          label: this.translateOrFallback('Year', 'Year'),
          showClear: false,
          defaultVisible: true,
          hideable: false,
        },
        {
          type: 'select',
          formControlName: 'month',
          label: this.translateOrFallback('Month', 'Month'),
          options: monthFilterOptions,
          showClear: false,
          defaultVisible: true,
          hideable: false,
        },
        ...costSourceFilters,
        {
          type: 'multiselect',
          formControlName: 'orderTypes',
          label: this.translateOrFallback('Cost_Analysis_Filter_Order_Type', 'Order Type'),
          options: this.orderTypeFilterOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };
  }

  handleApply(): void {
    if (this.yearControl.invalid || this.monthControl.invalid) return;
    this.load();
  }

  handleReset(): void {
    this.clearAllFilters();
    this.load();
  }

  private syncFilterControls(filters: DynamicFilterValues): void {
    const mounting = !this.filtersMounted;
    this.filtersMounted = true;
    const options = { emitEvent: !mounting };
    const year = filters['year'] as number | null;
    const month = filters['month'] as number | null;
    const costSource = filters['costSource'] as CostSourceFilter | null;
    const orderTypes = filters['orderTypes'] as OrderType[] | null;
    if (year != null) this.yearControl.setValue(year, options);
    if (month != null) this.monthControl.setValue(month, options);
    if (costSource != null && this.canViewRealCosts) this.costSourceControl.setValue(costSource, options);
    if (orderTypes != null || !mounting) this.orderTypeFilterControl.setValue(orderTypes ?? [], options);
  }

  private startLoading(): void {
    this.loadingCount.update((c) => c + 1);
  }

  private stopLoading(): void {
    this.loadingCount.update((c) => Math.max(0, c - 1));
  }

  private handlePeriodSummaryError(err: unknown): void {
    if (this.isNotCalculatedError(err)) {
      this.notCalculated.set(true);
      return;
    }
    console.error('Failed to load period summary', err);
    GlobalMessageUtil.error('Cost Analysis', this.translate.instant('Cost_Analysis_Load_Failed'));
  }

  private isNotCalculatedError(err: unknown): boolean {
    const e = err as { status?: number; error?: { errorMessage?: string | { en?: string } } } | null;
    if (e?.status === 404) return true;
    const raw = e?.error?.errorMessage;
    const message = typeof raw === 'string' ? raw : (raw?.en ?? '');
    return message.toLowerCase().includes('no iteration found');
  }

  private applyPeriodSummary(ps: PeriodSummaryReportDto | null): void {
    this.periodSummary.set(ps);
    if (ps) {
      this.periodTotals.set({
        rollCount: ps.rollCount,
        productionLbs: ps.productionLbs,
        processedLbs: ps.processedLbs,
      });
      this.state.excludedCounts.set(ps.excludedCounts);
    } else {
      this.periodTotals.set({
        rollCount: 0,
        productionLbs: 0,
        processedLbs: 0,
      });
      this.state.excludedCounts.set({ greige: 0, yarn: 0, unknown: 0 });
    }
  }

  hasActiveFilters(): boolean {
    return filtersAreActive(this.state.filters());
  }

  clearAllFilters(): void {
    this.fabricFilterControl.setValue([]);
    this.colorFilterControl.setValue([]);
    this.productionCardFilterControl.setValue([]);
    this.workOrderFilterControl.setValue([]);
    this.rollFilterControl.setValue([]);
    this.clientFilterControl.setValue([]);
    this.orderTypeFilterControl.setValue([]);
    this.onlyCompletedRollsControl.setValue(false);
  }

  private reloadPeriodSummary(): void {
    const year = this._currentYear();
    const month = this._currentMonth();
    if (!year || !month) return;
    const src = this.state.costSourceFilter();
    this.startLoading();
    this.costReportsCache
      .getPeriodSummary(year, month, src, this.state.filters().onlyCompletedRolls)
      .pipe(
        catchError((err) => {
          this.handlePeriodSummaryError(err);
          return of({ success: false, data: null, errorMessage: null });
        }),
        finalize(() => this.stopLoading()),
      )
      .subscribe((resp) => {
        this.applyPeriodSummary(resp.success && resp.data ? resp.data : null);
      });
  }

  onProcessClick(context: ProcessDetailContext): void {
    this.state.scrollY.set(window.scrollY);
    this.router.navigate(['/cost-analysis/detail'], {
      state: { detailContext: context },
    });
  }
}
